import { CarIcon, GroupPeople, HouseIcon } from 'assets/icons';
import Tooltips from 'components/tooltips/Tooltips';
import { EVENT_CATEGORIES, eventCategoryOf } from 'constants/eventCategories';
import { clockLabel, dayLabel, isoDay } from 'helper/timeLabels';
import useScrolledShadow from 'hooks/useScrolledShadow';
import useText from 'hooks/useText';
import { FIRST_HOUR, LAST_HOUR, MY_SCHEDULE, lanes, sameDay, startOfDay } from 'pages/planning/planningItems';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const ROW = 60; // px an hour
const DAY_NAMES = ['DIM.', 'LUN.', 'MAR.', 'MER.', 'JEU.', 'VEN.', 'SAM.'];
const minutesOf = (date) => date.getHours() * 60 + date.getMinutes();
// The small sign of an appointment the person of the week is invited to.
const InvitationSign = ({ label }) => (
  <span role="img" aria-label={label} title={label} className="mr-1 inline-flex align-[-1px] text-gray-600">
    <GroupPeople wh={12} color="currentColor" />
  </span>
);
const atMinute = (day, minutes) => {
  const date = new Date(day);
  date.setHours(0, minutes, 0, 0);
  return date;
};

// The quarter hour under the pointer in a day's column, in minutes from midnight.
const quarterAt = (event, column, first, last) => {
  const y = event.clientY - column.getBoundingClientRect().top;
  return first * 60 + Math.max(0, Math.min((last - first) * 60 - 15, Math.floor((y / ROW) * 4) * 15));
};

// The window that creates beside the slot: on its right, else on its left,
// else over it. Fixed, it follows the slot and no scrolling grid clips it.
const Floating = ({ anchor, watch, children }) => {
  const box = useRef(null);
  useLayoutEffect(() => {
    const place = () => {
      const slot = anchor.current?.getBoundingClientRect();
      const element = box.current;
      if (!slot || !element) return;
      const width = element.offsetWidth;
      let left = slot.right + 12;
      if (left + width > window.innerWidth - 8) left = slot.left - 12 - width;
      if (left < 8) left = Math.max(8, Math.min(window.innerWidth - width - 8, slot.left + (slot.width - width) / 2));
      element.style.left = `${left}px`;
      element.style.top = `${Math.max(8, Math.min(window.innerHeight - element.offsetHeight - 8, slot.top))}px`;
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [anchor, watch]);
  return (
    <div ref={box} data-no-pick className="fixed z-40" style={{ left: -9999, top: 0 }}>
      {children}
    </div>
  );
};

/**
 * The week of the planning, Monday to Sunday (or a day), as on the
 * prototype. `items`: { id, title, titleTime, subtitle, start, end, category,
 * past, travelAfter, flash, google, plain, look, invitation, masked, onClick };
 * `look` ({ className, style }, appointmentLook) replaces the tone of the
 * category. `dragging`: the visit
 * being dragged ({ minutes, title, city }), dropped with onDrop(day, start).
 * `allDay`: under each date, the appointments of the whole day or of several
 * days ({ id, title, time, category, google, appointment, look, invitation,
 * masked }) and the tasks
 * without a time. `firstHour` to `lastHour`: the hours drawn. Creation as in Google
 * Calendar: a click on a free slot, or a drag across quarters, calls
 * onDraft({ start, minutes }); `draft` shows dashed, `popover` beside it.
 */
const WeekGrid = ({ days, items, dragging, onDrop, highlight, allDay, draft, onDraft, popover, firstHour = FIRST_HOUR, lastHour = LAST_HOUR }) => {
  const { text } = useText('planning');
  const [now, setNow] = useState(() => new Date());
  const [ghost, setGhost] = useState(null);
  // The slot being drawn before release: { day, from, to }, quarters in minutes.
  const [pick, setPick] = useState(null);
  const draftRef = useRef(null);
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!dragging) setGhost(null);
  }, [dragging]);

  const { ref: watchScroll, isScrolled } = useScrolledShadow();
  const hours = Array.from({ length: lastHour - firstHour }, (_, index) => firstHour + index);
  const topOf = (date) => Math.max(0, ((minutesOf(date) - firstHour * 60) / 60) * ROW);

  const slotAt = (day, event) => {
    const y = event.clientY - event.currentTarget.getBoundingClientRect().top;
    const minutes = firstHour * 60 + Math.round(((y / ROW) * 60) / 15) * 15 - 15;
    const clamped = Math.max(firstHour * 60, Math.min(lastHour * 60 - (dragging?.minutes ?? 45), minutes));
    return atMinute(day, clamped);
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex shrink-0 pl-11">
        {days.map((day) => {
          const today = sameDay(day, now);
          return (
            <div key={day.toISOString()} className="flex min-w-0 flex-1 items-center justify-center gap-2 py-3">
              <span className={`text-xs ${today ? 'font-medium text-yellow-500' : 'text-gray-400'}`}>{DAY_NAMES[day.getDay()]}</span>
              {today ? (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-yellow-500 text-sm font-medium text-white">
                  {day.getDate()}
                </span>
              ) : (
                <span className={`text-sm font-medium ${day < startOfDay(now) ? 'text-gray-400' : 'text-gray-900'}`}>{day.getDate()}</span>
              )}
              {/* On seven columns, working from home fits in its icon. */}
              {MY_SCHEDULE.remoteDays.includes(day.getDay()) &&
                (days.length > 1 ? (
                  <Tooltips text={text('remote')} placement="top">
                    <span role="img" aria-label={text('remote')} className="flex items-center rounded-md bg-gray-100 p-1 text-gray-600">
                      <HouseIcon wh={12} color="currentColor" />
                    </span>
                  </Tooltips>
                ) : (
                  <span className="flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    <HouseIcon wh={12} color="currentColor" />
                    {text('remote')}
                  </span>
                ))}
            </div>
          );
        })}
      </div>
      <div
        className={`relative z-10 flex shrink-0 border-b border-gray-100 pb-2 pl-11 transition-shadow duration-150 ${isScrolled ? 'shadow-[0_4px_6px_-4px_rgba(17,24,39,0.12)]' : ''}`}
      >
        {days.map((day) => {
          const list = allDay?.[isoDay(day)] ?? [];
          return (
            <div
              key={day.toISOString()}
              className="flex min-h-[30px] min-w-0 flex-1 flex-col gap-1 px-1"
              onClick={
                onDraft &&
                ((event) => {
                  if (!event.target.closest('button, [data-no-pick]')) onDraft({ start: startOfDay(day), allDay: true });
                })
              }
            >
              {list.slice(0, 2).map((entry) => {
                let tone = 'border border-gray-200 bg-white hover:bg-gray-50';
                if (entry.google) tone = 'border border-[#0b8043]/30 bg-[#e6f4ea] hover:ring-1 hover:ring-inset hover:ring-gray-900/25';
                else if (entry.appointment) tone = `${EVENT_CATEGORIES[eventCategoryOf(entry.category)].block} hover:ring-1 hover:ring-inset hover:ring-gray-900/25`;
                if (entry.look) tone = `${entry.look.className} hover:ring-1 hover:ring-inset hover:ring-gray-900/25`;
                let textTone = 'text-gray-900';
                if (entry.done) textTone = 'text-gray-400 line-through';
                else if (entry.masked) textTone = 'text-gray-600';
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={entry.onClick}
                    style={entry.look?.style}
                    className={`truncate rounded-md px-2 py-1 text-left text-xs font-medium transition ${tone} ${textTone}`}
                  >
                    {entry.invitation && <InvitationSign label={text('invitation')} />}
                    {entry.time ? `${entry.time} ` : ''}
                    {entry.title}
                  </button>
                );
              })}
              {list.length > 2 && (
                <span data-no-pick className="px-2 text-xs text-gray-600">
                  {text(list.length > 3 ? 'othersMany' : 'othersOne', { count: list.length - 2 })}
                </span>
              )}
              {draft?.allDay && sameDay(draft.start, day) && (
                <span
                  ref={draftRef}
                  data-no-pick
                  className="truncate rounded-md border-2 border-dashed border-yellow-500 bg-yellow-50 px-2 py-0.5 text-xs font-medium text-gray-900"
                >
                  {draft.title || text('untitled')}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div ref={watchScroll} className="thin-scrollbar min-h-0 flex-1 overflow-y-auto">
        <div className="relative flex select-none pt-2" style={{ height: (lastHour - firstHour) * ROW + 16 }}>
          <div className="w-11 shrink-0">
            {hours.map((hour) => {
              // The start and the end of duty: an orange dot on the axis.
              const shift = hour === MY_SCHEDULE.shift[0] ? 'shiftStart' : hour === MY_SCHEDULE.shift[1] ? 'shiftEnd' : null;
              return (
                <div key={hour} className="relative text-right text-xs text-gray-400" style={{ height: ROW }}>
                  {shift ? (
                    <Tooltips text={text(shift, { hour })} placement="right">
                      <span className="absolute -top-2 right-0 z-10 flex items-center gap-1.5 font-medium text-yellow-500">
                        {hour}h <span className="h-2 w-2 translate-x-1 rounded-full bg-yellow-500" />
                      </span>
                    </Tooltips>
                  ) : (
                    <span className="absolute -top-2 right-3">{hour}h</span>
                  )}
                </div>
              );
            })}
          </div>
          {days.map((day) => {
            const dayItems = lanes(items.filter((item) => sameDay(item.start, day)));
            const today = sameDay(day, now);
            const showGhost = ghost && sameDay(ghost.start, day);
            const picking = pick && sameDay(pick.day, day);
            // The slot being drawn, else the one waiting for its title.
            const shown = picking
              ? { start: atMinute(day, Math.min(pick.from, pick.to)), minutes: Math.abs(pick.to - pick.from) + 15 }
              : draft && !draft.allDay && sameDay(draft.start, day)
                ? draft
                : null;
            return (
              <div
                key={day.toISOString()}
                className={`relative flex-1 border-l border-gray-100 ${highlight && sameDay(highlight, day) ? 'bg-yellow-50/40' : ''}`}
                onDragOver={(event) => {
                  if (!dragging) return;
                  event.preventDefault();
                  const start = slotAt(day, event);
                  if (!ghost || +ghost.start !== +start) setGhost({ start });
                }}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setGhost(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const start = slotAt(day, event);
                  setGhost(null);
                  onDrop?.(day, start);
                }}
                onPointerDown={
                  onDraft &&
                  ((event) => {
                    if (event.button !== 0 || event.target.closest('button, a, [data-no-pick]')) return;
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    const minutes = quarterAt(event, event.currentTarget, firstHour, lastHour);
                    setPick({ day, from: minutes, to: minutes });
                  })
                }
                onPointerMove={
                  picking
                    ? (event) => {
                        const minutes = quarterAt(event, event.currentTarget, firstHour, lastHour);
                        if (minutes !== pick.to) setPick({ ...pick, to: minutes });
                      }
                    : undefined
                }
                onPointerUp={
                  picking
                    ? (event) => {
                        const to = quarterAt(event, event.currentTarget, firstHour, lastHour);
                        setPick(null);
                        // A click: the length follows the type; a drag sets it.
                        onDraft({ start: atMinute(day, Math.min(pick.from, to)), minutes: to === pick.from ? null : Math.abs(to - pick.from) + 15 });
                      }
                    : undefined
                }
                onPointerCancel={() => setPick(null)}
              >
                {hours.map((hour) => (
                  <div key={hour} className="border-b border-gray-100" style={{ height: ROW }} />
                ))}
                {dayItems.map((item) => {
                  const category = EVENT_CATEGORIES[eventCategoryOf(item.category)];
                  const height = Math.max(26, topOf(item.end) - topOf(item.start) - 4);
                  const compact = height < 44;
                  let tone = category.block;
                  if (item.google) tone = 'border border-[#0b8043]/30 bg-[#e6f4ea]';
                  else if (item.plain) tone = 'border border-gray-200 bg-white';
                  if (item.look) tone = item.look.className;
                  if (item.danger) tone = 'border border-dashed border-red-500 bg-red-50';
                  let titleTone = 'text-gray-900';
                  if (item.past || item.masked) titleTone = 'text-gray-600';
                  return (
                    <div
                      key={item.id}
                      className="absolute"
                      style={{
                        top: topOf(item.start) + 2,
                        height,
                        left: `calc(${(item.col / item.cols) * 100}% + 4px)`,
                        width: `calc(${100 / item.cols}% - 8px)`,
                      }}
                    >
                      <button
                        type="button"
                        onClick={item.onClick}
                        style={item.look?.style}
                        className={`h-full w-full overflow-hidden rounded-lg px-2 py-1.5 text-left transition hover:ring-1 hover:ring-inset hover:ring-gray-900/25 ${tone} ${
                          item.past ? 'opacity-60' : ''
                        } ${item.flash ? 'ring-2 ring-yellow-500' : ''}`}
                      >
                        <span className={`block truncate text-[13px] font-medium leading-4 ${titleTone}`}>
                          {item.invitation && <InvitationSign label={text('invitation')} />}
                          {item.title}
                          {compact && item.titleTime ? ` · ${item.titleTime}` : ''}
                        </span>
                        {!compact && (
                          <span className="block truncate text-xs leading-4 text-gray-600">
                            {clockLabel(item.start)}
                            {item.subtitle ? ` · ${item.subtitle}` : ''}
                          </span>
                        )}
                      </button>
                      {item.travelAfter ? (
                        <span className="absolute left-1 flex items-center gap-1 text-xs text-gray-400" style={{ top: height + 4 }}>
                          <span className="flex text-gray-900">
                            <CarIcon wh={12} color="currentColor" />
                          </span>
                          {text('travel', { minutes: item.travelAfter })}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
                {shown && (
                  <div ref={picking ? undefined : draftRef} data-no-pick className="absolute left-1 right-1 z-20" style={{ top: topOf(shown.start) + 2 }}>
                    <div
                      className="overflow-hidden rounded-lg border-2 border-dashed border-yellow-500 bg-yellow-50 px-2 py-1"
                      style={{ height: Math.max(26, (Math.min(shown.minutes, lastHour * 60 - minutesOf(shown.start)) / 60) * ROW - 4) }}
                    >
                      <span className="block truncate text-[13px] font-medium leading-4 text-gray-900">{shown.title || text('untitled')}</span>
                      <span className="block truncate text-xs leading-4 text-gray-600">
                        {clockLabel(shown.start)} – {clockLabel(new Date(shown.start.getTime() + shown.minutes * 60000))}
                      </span>
                    </div>
                  </div>
                )}
                {showGhost && dragging && (
                  <div className="pointer-events-none absolute left-1 right-1 z-20" style={{ top: topOf(ghost.start) + 2 }}>
                    <span className="absolute -top-9 left-0 whitespace-nowrap rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white">
                      {dayLabel(ghost.start)} · {clockLabel(ghost.start)} – {clockLabel(new Date(ghost.start.getTime() + dragging.minutes * 60000))}
                    </span>
                    <div
                      className="rounded-lg border-2 border-dashed border-yellow-500 bg-yellow-50 px-2 py-1.5"
                      style={{ height: Math.max(26, (dragging.minutes / 60) * ROW - 4) }}
                    >
                      <span className="block truncate text-[13px] font-medium text-gray-900">{dragging.title}</span>
                      <span className="block truncate text-xs text-gray-600">
                        {clockLabel(ghost.start)}
                        {dragging.city ? ` · ${dragging.city}` : ''}
                      </span>
                    </div>
                  </div>
                )}
                {today && minutesOf(now) >= firstHour * 60 && minutesOf(now) < lastHour * 60 && (
                  <div className="pointer-events-none absolute inset-x-0 z-10 border-t-2 border-red-500" style={{ top: topOf(now) }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      {draft && popover && !pick && (
        <Floating anchor={draftRef} watch={`${+draft.start}-${draft.minutes}-${Boolean(draft.allDay)}`}>
          {popover}
        </Floating>
      )}
    </div>
  );
};

export default WeekGrid;
